<script setup lang="ts">
import { usePreferredReducedMotion } from '@vueuse/core';
import { TransitionDuration } from '@/modules/common/enums/transition-duration.enum.ts';

const SPACING = ['marginTop', 'marginBottom', 'paddingTop', 'paddingBottom'] as const;

type Spacing = (typeof SPACING)[number];

type Box = Record<Spacing | 'height' | 'opacity', number>;

const { group = false, tag = 'div' } = defineProps<{
  group?: boolean;
  tag?: string;
}>();

defineSlots<{
  default: () => unknown;
}>();

const TRANSITION = ['height', 'opacity', 'margin-top', 'margin-bottom', 'padding-top', 'padding-bottom']
  .map((property) => `${property} ${TransitionDuration.Normal}ms ease-in-out`)
  .join(', ');

const COLLAPSED: Box = { height: 0, opacity: 0, marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 };

const reducedMotion = usePreferredReducedMotion();

const endListeners = new WeakMap<HTMLElement, (event: TransitionEvent) => void>();

function stopListening(element: HTMLElement) {
  const listener = endListeners.get(element);

  if (listener) {
    element.removeEventListener('transitionend', listener);
    endListeners.delete(element);
  }
}

function applyBox(element: HTMLElement, box: Box) {
  element.style.height = `${box.height}px`;
  element.style.opacity = String(box.opacity);
  SPACING.forEach((property) => (element.style[property] = `${box[property]}px`));
}

function measure(element: HTMLElement): Box {
  const style = getComputedStyle(element);
  const box = { height: element.getBoundingClientRect().height, opacity: Number(style.opacity) } as Box;

  SPACING.forEach((property) => (box[property] = Number.parseFloat(style[property]) || 0));

  return box;
}

function getContentHeight(element: HTMLElement) {
  const style = getComputedStyle(element);

  return (
    element.scrollHeight +
    (Number.parseFloat(style.borderTopWidth) || 0) +
    (Number.parseFloat(style.borderBottomWidth) || 0)
  );
}

function animate(element: HTMLElement, from: Box, to: Box, done: () => void) {
  stopListening(element);

  if (reducedMotion.value === 'reduce' || Math.abs(from.height - to.height) < 1) {
    done();
    return;
  }

  element.style.transition = 'none';
  element.style.overflow = 'hidden';
  applyBox(element, from);
  void element.offsetHeight;

  element.style.transition = TRANSITION;
  applyBox(element, to);

  const listener = (event: TransitionEvent) => {
    if (event.target === element && event.propertyName === 'height') {
      stopListening(element);
      done();
    }
  };

  endListeners.set(element, listener);
  element.addEventListener('transitionend', listener);
}

function onEnter(element: Element, done: () => void) {
  const target = element as HTMLElement;
  const expanded = { ...measure(target), opacity: 1 };

  const settle = () => {
    const height = getContentHeight(target);

    if (Math.abs(height - target.getBoundingClientRect().height) < 1) {
      done();
      return;
    }

    animate(target, measure(target), { ...measure(target), height }, settle);
  };

  animate(target, COLLAPSED, expanded, settle);
}

function onLeave(element: Element, done: () => void) {
  const target = element as HTMLElement;

  animate(target, measure(target), COLLAPSED, done);
}

function clean(element: Element) {
  const target = element as HTMLElement;

  stopListening(target);
  target.style.transition = '';
  target.style.overflow = '';
  target.style.height = '';
  target.style.opacity = '';
  SPACING.forEach((property) => (target.style[property] = ''));
}
</script>

<template>
  <TransitionGroup
    v-if="group"
    :tag
    :css="false"
    @enter="onEnter"
    @after-enter="clean"
    @leave="onLeave"
    @after-leave="clean"
  >
    <slot />
  </TransitionGroup>

  <Transition
    v-else
    :css="false"
    @enter="onEnter"
    @after-enter="clean"
    @leave="onLeave"
    @after-leave="clean"
  >
    <slot />
  </Transition>
</template>
